import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import DatePicker from "~/molecules/DatePicker";
import { BulkEditableCell } from "~/molecules/BulkEditableCell";
import { formatDate } from "~/helpers/date";
import { toggleSetItem } from "~/helpers/set";
import {
  buildWorkflowDateChain,
  getDateChainBounds,
  getStepDateIssues,
  isBeforeToday,
  type DateChainBounds,
  type StepDateIssue,
  type WorkflowDateKey,
} from "~/helpers/workflow";

export interface ApplyDocument {
  id: number;
  title: string;
  reviewFrom: Date | null;
  reviewTo: Date | null;
}

export interface ApplyStep {
  name: string;
  order: number;
}

export interface DueDateRow {
  reviewFrom: Date | null;
  reviewTo: Date | null;
  stepDueDates: (Date | null)[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  steps: ApplyStep[];
  documents: ApplyDocument[];
  onApply: (rows: Record<number, DueDateRow>) => void;
  isApplying: boolean;
}

export function ApplyTemplateDatesDialog({
  open,
  onOpenChange,
  templateName,
  steps,
  documents,
  onApply,
  isApplying,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <ApplyTemplateDatesDialogContent
          templateName={templateName}
          steps={steps}
          documents={documents}
          onApply={onApply}
          isApplying={isApplying}
          onOpenChange={onOpenChange}
        />
      )}
    </Dialog>
  );
}

interface DateCellProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDueAt?: string;
  maxDueAt?: string;
  warningMessage?: string;
  hasError?: boolean;
  highlighted: boolean;
  onHover: () => void;
  onHoverLeave: () => void;
  errorMessage?: string;
}

const DateCell = memo(function DateCell({
  value,
  onChange,
  minDueAt,
  maxDueAt,
  warningMessage,
  hasError,
  highlighted,
  onHover,
  onHoverLeave,
  errorMessage,
}: DateCellProps) {
  return (
    <TableCell className="px-3 py-2">
      <BulkEditableCell
        hasError={hasError}
        highlighted={highlighted}
        onHover={onHover}
        onHoverLeave={onHoverLeave}
        errorMessage={errorMessage}
        warningMessage={warningMessage}
      >
        <DatePicker
          value={value}
          onChange={(date) => onChange(date ?? null)}
          minDate={minDueAt ? new Date(minDueAt) : undefined}
          maxDate={maxDueAt ? new Date(maxDueAt) : undefined}
        />
      </BulkEditableCell>
    </TableCell>
  );
});

interface DocumentDateRowProps {
  doc: ApplyDocument;
  row: DueDateRow;
  steps: ApplyStep[];
  datesConfirmed: boolean;
  isSelected: boolean;
  rowIssues: Map<number, StepDateIssue> | undefined;
  rowBounds: Map<WorkflowDateKey, DateChainBounds> | undefined;
  onUpdateCell: (
    field: "reviewFrom" | "reviewTo" | number,
    value: Date | null,
  ) => void;
  onToggleSelection: () => void;
  onHoverCell: (col: number) => void;
  onHoverLeave: () => void;
  isBulkHighlighted: (col: number) => boolean;
}

const DocumentDateRow = memo(function DocumentDateRow({
  doc,
  row,
  steps,
  datesConfirmed,
  isSelected,
  rowIssues,
  rowBounds,
  onUpdateCell,
  onToggleSelection,
  onHoverCell,
  onHoverLeave,
  isBulkHighlighted,
}: DocumentDateRowProps) {
  const { t } = useTranslation();

  if (datesConfirmed) {
    return (
      <TableRow>
        <TableCell className="px-3 py-2 font-medium">{doc.title}</TableCell>
        <TableCell className="text-muted-foreground px-3 py-2">
          {formatDate(row.reviewFrom)}
        </TableCell>
        {steps.map((step, stepIndex) => (
          <TableCell
            key={step.order}
            className="text-muted-foreground px-3 py-2"
          >
            {formatDate(row.stepDueDates[stepIndex])}
          </TableCell>
        ))}
        <TableCell className="text-muted-foreground px-3 py-2">
          {formatDate(row.reviewTo)}
        </TableCell>
      </TableRow>
    );
  }

  const errorMsg = (stepIndex: number): string | undefined => {
    const issue = rowIssues?.get(stepIndex);
    if (!issue) return undefined;
    return issue === "afterReviewTo"
      ? t(($) => $.commons.workflow.responseDateAfterReviewToWarning)
      : t(($) => $.commons.workflow.responseDateWarning);
  };

  return (
    <TableRow>
      <TableCell className="px-3 py-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelection}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: doc.title,
          })}
        />
      </TableCell>
      <TableCell className="px-3 py-2 font-medium">{doc.title}</TableCell>
      <DateCell
        value={row.reviewFrom}
        onChange={(date) => onUpdateCell("reviewFrom", date)}
        minDueAt={rowBounds?.get("reviewFrom")?.minDueAt}
        maxDueAt={rowBounds?.get("reviewFrom")?.maxDueAt}
        warningMessage={
          isBeforeToday(row.reviewFrom)
            ? t(($) => $.commons.workflow.reviewFromPastWarning)
            : undefined
        }
        highlighted={isBulkHighlighted(0)}
        onHover={() => onHoverCell(0)}
        onHoverLeave={onHoverLeave}
      />
      {steps.map((step, stepIndex) => (
        <DateCell
          key={step.order}
          value={row.stepDueDates[stepIndex] ?? null}
          onChange={(date) => onUpdateCell(stepIndex, date)}
          hasError={rowIssues?.has(stepIndex) ?? false}
          minDueAt={rowBounds?.get(stepIndex)?.minDueAt}
          maxDueAt={rowBounds?.get(stepIndex)?.maxDueAt}
          highlighted={isBulkHighlighted(stepIndex + 1)}
          onHover={() => onHoverCell(stepIndex + 1)}
          onHoverLeave={onHoverLeave}
          errorMessage={errorMsg(stepIndex)}
        />
      ))}
      <DateCell
        value={row.reviewTo}
        onChange={(date) => onUpdateCell("reviewTo", date)}
        minDueAt={rowBounds?.get("reviewTo")?.minDueAt}
        maxDueAt={rowBounds?.get("reviewTo")?.maxDueAt}
        warningMessage={
          isBeforeToday(row.reviewTo)
            ? t(($) => $.commons.workflow.reviewToPastWarning)
            : undefined
        }
        highlighted={isBulkHighlighted(steps.length + 1)}
        onHover={() => onHoverCell(steps.length + 1)}
        onHoverLeave={onHoverLeave}
      />
    </TableRow>
  );
});

type ContentProps = Omit<Props, "open">;

function ApplyTemplateDatesDialogContent({
  templateName,
  steps,
  documents,
  onApply,
  isApplying,
  onOpenChange,
}: ContentProps) {
  const { t } = useTranslation();

  const [datesConfirmed, setDatesConfirmed] = useState(false);

  const [rows, setRows] = useState<Map<number, DueDateRow>>(() => {
    const map = new Map<number, DueDateRow>();
    for (const doc of documents) {
      map.set(doc.id, {
        reviewFrom: doc.reviewFrom,
        reviewTo: doc.reviewTo,
        stepDueDates: steps.map(() => null),
      });
    }
    return map;
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const updateCell = useCallback(
    (
      docId: number,
      field: "reviewFrom" | "reviewTo" | number,
      value: Date | null,
    ) => {
      setRows((prev) => {
        const next = new Map(prev);

        const targetIds =
          selectedIds.has(docId) && selectedIds.size > 1
            ? [...selectedIds]
            : [docId];

        for (const targetId of targetIds) {
          const row = next.get(targetId);
          if (!row) continue;

          if (typeof field === "number") {
            const newStepDueDates = [...row.stepDueDates];
            newStepDueDates[field] = value;
            next.set(targetId, { ...row, stepDueDates: newStepDueDates });
          } else {
            next.set(targetId, { ...row, [field]: value });
          }
        }

        return next;
      });
    },
    [selectedIds],
  );

  const toggleSelection = useCallback((docId: number) => {
    setSelectedIds((prev) => toggleSetItem(prev, docId));
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === documents.length) {
        return new Set();
      }
      return new Set(documents.map((d) => d.id));
    });
  }, [documents]);

  const allSelected =
    documents.length > 0 && selectedIds.size === documents.length;

  const [hoveredCell, setHoveredCell] = useState<{
    docId: number;
    col: number;
  } | null>(null);

  const isBulkHighlighted = useCallback(
    (docId: number, col: number) =>
      hoveredCell !== null &&
      hoveredCell.col === col &&
      selectedIds.size > 1 &&
      selectedIds.has(hoveredCell.docId) &&
      selectedIds.has(docId),
    [hoveredCell, selectedIds],
  );

  const { validationErrors, dateBounds } = useMemo(() => {
    const errors = new Map<number, Map<number, StepDateIssue>>();
    const bounds = new Map<number, Map<WorkflowDateKey, DateChainBounds>>();

    for (const [docId, row] of rows) {
      const chain = buildWorkflowDateChain(
        row.stepDueDates.map((date) => ({ dueAt: date })),
        row.reviewFrom,
        row.reviewTo,
      );

      const issues = getStepDateIssues(chain);
      if (issues.size > 0) errors.set(docId, issues);
      bounds.set(docId, getDateChainBounds(chain));
    }

    return { validationErrors: errors, dateBounds: bounds };
  }, [rows]);

  const hasValidationErrors = validationErrors.size > 0;

  const handleApply = useCallback(() => {
    onApply(Object.fromEntries(rows));
  }, [rows, onApply]);

  return (
    <DialogContent className="w-fit max-w-[90vw] sm:max-w-[90vw]">
      <DialogHeader>
        <DialogTitle>{templateName}</DialogTitle>
        <DialogDescription>
          {datesConfirmed
            ? t(($) => $.workflowTemplates.applyDatesDialog.confirmDescription)
            : t(($) => $.workflowTemplates.applyDatesDialog.description)}
        </DialogDescription>
      </DialogHeader>

      <Table
        className="border-collapse"
        wrapperClassName="w-max max-w-[calc(90vw-2rem)] max-h-[60vh] rounded-md border"
      >
        <TableHeader className="bg-muted sticky top-0 z-10">
          <TableRow>
            {!datesConfirmed && (
              <TableHead className="w-10 px-3 py-2">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label={t(($) => $.commons.buttons.selectAll)}
                />
              </TableHead>
            )}
            <TableHead className="min-w-50 px-3 py-2">
              {t(($) => $.workflowTemplates.applyDatesDialog.documentTitle)}
            </TableHead>
            <TableHead className="min-w-45 px-3 py-2">
              {t(($) => $.commons.document.reviewFrom)}
            </TableHead>
            {steps.map((step) => (
              <TableHead key={step.order} className="min-w-45 px-3 py-2">
                {step.name}
              </TableHead>
            ))}
            <TableHead className="min-w-45 px-3 py-2">
              {t(($) => $.commons.document.reviewTo)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => {
            const row = rows.get(doc.id);
            if (!row) return null;

            return (
              <DocumentDateRow
                key={doc.id}
                doc={doc}
                row={row}
                steps={steps}
                datesConfirmed={datesConfirmed}
                isSelected={selectedIds.has(doc.id)}
                rowIssues={validationErrors.get(doc.id)}
                rowBounds={dateBounds.get(doc.id)}
                onUpdateCell={(field, value) =>
                  updateCell(doc.id, field, value)
                }
                onToggleSelection={() => toggleSelection(doc.id)}
                onHoverCell={(col) => setHoveredCell({ docId: doc.id, col })}
                onHoverLeave={() => setHoveredCell(null)}
                isBulkHighlighted={(col) => isBulkHighlighted(doc.id, col)}
              />
            );
          })}
        </TableBody>
      </Table>

      <DialogFooter>
        {datesConfirmed ? (
          <>
            <Button
              variant="outline"
              onClick={() => setDatesConfirmed(false)}
              disabled={isApplying}
            >
              {t(($) => $.commons.buttons.back)}
            </Button>
            <Button onClick={handleApply} disabled={isApplying}>
              {isApplying
                ? t(($) => $.workflowTemplates.applying)
                : t(($) => $.workflowTemplates.applyDatesDialog.apply)}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button
              onClick={() => setDatesConfirmed(true)}
              disabled={hasValidationErrors}
            >
              {t(($) => $.workflowTemplates.applyDatesDialog.confirmDates)}
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
